import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import {
  getVoiceSelectionCatalog,
  getVoiceSelectionSettings,
  previewVoiceSelection,
  saveVoiceSelection,
} from '../../db/voice-selection-resolvers.js';

type VoiceSettings = {
  enabled: boolean;
  canSet: boolean;
  defaultVoice: string;
  currentVoice: string;
  source: 'saved' | 'environment';
  updatedAt: string | null;
};
type Voice = {
  id: string;
  name: string;
  description: string;
  gender: string;
  source: 'shared' | 'custom';
  age: string;
  accent: string;
  useCase: string;
  style: string;
};
type Playground = {
  alive: boolean;
  loadVersion: number;
  previewVersion: number;
  saving: boolean;
  previewId: string | null;
  audio: HTMLAudioElement | null;
  objectURL: string | null;
  observer: IntersectionObserver | null;
  visible: number;
};
const BATCH_SIZE = 24;
const metadataText = (value: unknown) =>
  typeof value === 'string'
    ? value
    : Array.isArray(value)
      ? value.filter(item => typeof item === 'string').join(', ')
      : '';
function parseSettings(raw: unknown): VoiceSettings {
  const value = typeof raw === 'string' ? JSON.parse(raw) : null;
  if (!value || typeof value.enabled !== 'boolean' || typeof value.canSet !== 'boolean')
    throw new Error('Invalid voice settings');
  if (
    value.enabled &&
    (!value.currentVoice ||
      typeof value.currentVoice !== 'string' ||
      !value.defaultVoice ||
      typeof value.defaultVoice !== 'string')
  )
    throw new Error('Missing voice selection');
  return {
    enabled: value.enabled,
    canSet: value.canSet,
    currentVoice: typeof value.currentVoice === 'string' ? value.currentVoice : '',
    defaultVoice: typeof value.defaultVoice === 'string' ? value.defaultVoice : '',
    source: value.source === 'saved' ? 'saved' : 'environment',
    updatedAt: typeof value.updatedAt === 'string' ? value.updatedAt : null,
  };
}
function parseVoices(raw: string): Voice[] {
  const value = JSON.parse(raw);
  if (!Array.isArray(value.voices)) throw new Error('Invalid voice catalog');
  const ids = new Set<string>();
  return value.voices.map((voice: Record<string, unknown>) => {
    if (!voice || typeof voice.id !== 'string' || !voice.id || ids.has(voice.id))
      throw new Error('Invalid voice catalog entry');
    ids.add(voice.id);
    return {
      id: voice.id,
      name: metadataText(voice.name) || voice.id,
      description: metadataText(voice.description),
      gender: metadataText(voice.gender),
      source: voice.source === 'custom' ? 'custom' : 'shared',
      age: metadataText(voice.age),
      accent: metadataText(voice.accent),
      useCase: metadataText(voice.useCase),
      style: metadataText(voice.style),
    };
  });
}
function releaseAudio(player: Playground) {
  if (player.audio) {
    player.audio.onended = null;
    player.audio.onerror = null;
    player.audio.pause();
    player.audio.removeAttribute('src');
    player.audio.load();
    player.audio = null;
  }
  if (player.objectURL) {
    URL.revokeObjectURL(player.objectURL);
    player.objectURL = null;
  }
}
const voiceIcon = () =>
  html`<svg
    width="23"
    height="23"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="1.7"
    stroke-linecap="round"
    aria-hidden="true"
  >
    <path d="M3 10v4M7 6v12M12 3v18M17 7v10M21 10v4"></path>
  </svg>`;
const playIcon = (active: boolean) =>
  active
    ? html`<svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <rect x="5" y="5" width="10" height="10" rx="1"></rect>
      </svg>`
    : html`<svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path d="M6 3.8a.8.8 0 0 1 1.2-.7l10 6.2a.8.8 0 0 1 0 1.4l-10 6.2A.8.8 0 0 1 6 16.2Z"></path>
      </svg>`;
const genderLabel = (value: string) => {
  const labels: Record<string, string> = {
    female: 'Weiblich',
    male: 'Männlich',
    neutral: 'Neutral',
    nonbinary: 'Nichtbinär',
  };
  return labels[value.toLowerCase()] || value;
};
const attributeLabel = (value: string) => value.replaceAll('_', ' ');
const catalogDate = (value: unknown) => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T/.test(value)) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
    : null;
};

const savedDate = (value: string | null) => {
  const date = value ? new Date(value) : null;
  return date && Number.isFinite(date.getTime()) ? date.toLocaleString('de-DE') : null;
};

// The server controls visibility as well as authorization of every operation.
export const VoicesNavigation = stateful(() => {
  const [reload, setReload] = useState(0);
  const query = useQuery(() => getVoiceSelectionSettings()(), [reload]);
  useEffect(() => {
    const refresh = () => setReload(Date.now());
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);
  try {
    if (!query.value || !parseSettings(query.value).enabled) return nothing;
  } catch {
    return nothing;
  }
  return html`<a
    class="flex items-center gap-1 hover:text-primary"
    href="/voices"
    title="Stimmen anhören und auswählen"
  >
    ${voiceIcon()}<span>Voices</span>
  </a>`;
});

export const VoicesPage = stateful(() => {
  const [settings, setSettings] = useState<VoiceSettings | null>(null);
  const [voices, setVoices] = useState<Voice[]>([]);
  const [catalogInfo, setCatalogInfo] = useState<{ bundled: boolean; date: string | null }>({
    bundled: false,
    date: null,
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const [savingVoice, setSavingVoice] = useState<string | null>(null);
  const [previewVoice, setPreviewVoice] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewText, setPreviewText] = useState('');
  const [player] = useState<Playground>({
    alive: true,
    loadVersion: 0,
    previewVersion: 0,
    saving: false,
    previewId: null,
    audio: null,
    objectURL: null,
    observer: null,
    visible: BATCH_SIZE,
  });

  const stopPreview = () => {
    ++player.previewVersion;
    player.previewId = null;
    releaseAudio(player);
    if (player.alive) {
      setPreviewVoice(null);
      setPreviewLoading(false);
    }
  };
  const reload = async () => {
    if (player.saving) return;
    const version = ++player.loadVersion;
    setLoading(true);
    setLoadError(null);
    try {
      const status = parseSettings(await getVoiceSelectionSettings()());
      if (!player.alive || player.loadVersion !== version) return;
      setSettings(status);
      if (!status.enabled) {
        setVoices([]);
        stopPreview();
        return;
      }
      const raw = await getVoiceSelectionCatalog()();
      if (!player.alive || player.loadVersion !== version) return;
      const nextSettings = parseSettings(raw);
      const catalog = parseVoices(raw);
      const metadata = JSON.parse(raw);
      setCatalogInfo({ bundled: metadata.catalogSource === 'bundled', date: catalogDate(metadata.capturedAt) });
      setSettings(nextSettings);
      setVoices(catalog);
      if (!nextSettings.enabled) stopPreview();
    } catch {
      if (player.alive && player.loadVersion === version)
        setLoadError('Stimmen konnten nicht geladen werden. Bitte versuchen Sie es erneut.');
    } finally {
      if (player.alive && player.loadVersion === version) setLoading(false);
    }
  };
  useEffect(() => {
    player.alive = true;
    void reload();
    const refresh = () => void reload();
    window.addEventListener('focus', refresh);
    return () => {
      player.alive = false;
      ++player.loadVersion;
      ++player.previewVersion;
      releaseAudio(player);
      player.observer?.disconnect();
      player.observer = null;
      window.removeEventListener('focus', refresh);
    };
  }, []);

  const preview = async (voice: Voice) => {
    if (player.previewId === voice.id) {
      stopPreview();
      return;
    }
    stopPreview();
    const version = ++player.previewVersion;
    player.previewId = voice.id;
    setPreviewVoice(voice.id);
    setPreviewLoading(true);
    setActionError(null);
    try {
      const raw = await previewVoiceSelection(voice.id)();
      if (!player.alive || player.previewVersion !== version) return;
      const result = JSON.parse(raw);
      if (
        result.voice !== voice.id ||
        result.mimeType !== 'audio/wav' ||
        typeof result.audioBase64 !== 'string' ||
        !result.audioBase64 ||
        typeof result.text !== 'string'
      )
        throw new Error('Invalid preview');
      const binary = atob(result.audioBase64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const objectURL = URL.createObjectURL(new Blob([bytes.buffer], { type: result.mimeType }));
      const audio = new Audio(objectURL);
      player.objectURL = objectURL;
      player.audio = audio;
      audio.onended = () => {
        if (player.audio === audio) stopPreview();
      };
      audio.onerror = () => {
        if (player.audio !== audio || !player.alive) return;
        stopPreview();
        setActionError('Die Hörprobe konnte nicht abgespielt werden. Bitte erneut versuchen.');
      };
      setPreviewText(result.text);
      setPreviewLoading(false);
      await audio.play();
    } catch {
      if (!player.alive || player.previewVersion !== version) return;
      stopPreview();
      setActionError('Die Hörprobe konnte nicht abgespielt werden. Bitte erneut versuchen.');
    }
  };
  const save = async (voice: Voice) => {
    if (!settings?.enabled || !settings.canSet || player.saving || voice.id === settings.currentVoice) return;
    player.saving = true;
    ++player.loadVersion; // A stale catalog must not overwrite a successful selection.
    setLoading(false);
    setSavingVoice(voice.id);
    setActionError(null);
    setNotice(null);
    try {
      const saved = parseSettings(await saveVoiceSelection(voice.id)());
      if (!player.alive) return;
      if (!saved.enabled || saved.currentVoice !== voice.id) throw new Error('Voice selection was not confirmed');
      setSettings(saved);
      setNotice(voice.name + ' ist für alle neuen Anrufe dieser Umgebung gespeichert.');
    } catch {
      if (player.alive)
        setActionError(
          'Speichern fehlgeschlagen. Die bisher bestätigte Auswahl bleibt angezeigt. Bitte erneut versuchen.',
        );
    } finally {
      player.saving = false;
      if (player.alive) setSavingVoice(null);
    }
  };

  const voiceFor = (id: string) =>
    voices.find(voice => voice.id === id) || {
      id,
      name: id,
      description: '',
      gender: '',
      source: 'shared' as const,
      age: '',
      accent: '',
      useCase: '',
      style: '',
    };
  // Original voice is permanently first, with the selected voice visible alongside it.
  const pinned = settings?.enabled
    ? [
        voiceFor(settings.defaultVoice),
        ...(settings.currentVoice !== settings.defaultVoice ? [voiceFor(settings.currentVoice)] : []),
      ]
    : [];
  const pinnedIDs = new Set(pinned.map(voice => voice.id));
  const term = search.trim().toLocaleLowerCase();
  const matches = voices.filter(
    voice =>
      !pinnedIDs.has(voice.id) &&
      (!term ||
        [voice.name, voice.id, voice.description, voice.gender, voice.age, voice.accent, voice.style, voice.useCase]
          .join(' ')
          .toLocaleLowerCase()
          .includes(term)),
  );
  const ordered = [...pinned, ...matches];
  const displayed = ordered.slice(0, visibleCount);
  const loadMore = () => {
    const next = Math.min(player.visible + BATCH_SIZE, ordered.length);
    if (player.alive && next > player.visible) {
      player.visible = next;
      setVisibleCount(next);
    }
  };
  const sentinel = (element?: Element) => {
    player.observer?.disconnect();
    player.observer = null;
    if (!element || typeof IntersectionObserver === 'undefined' || displayed.length >= ordered.length) return;
    const observer = new IntersectionObserver(
      entries => {
        if (player.alive && player.observer === observer && entries.some(entry => entry.isIntersecting)) loadMore();
      },
      { rootMargin: '240px' },
    );
    player.observer = observer;
    observer.observe(element);
  };
  const changeSearch = (value: string) => {
    setSearch(value);
    player.visible = BATCH_SIZE;
    setVisibleCount(BATCH_SIZE);
  };

  return html`
    <style>
      .zw-voices {
        max-width: 1480px;
        margin: 0 auto;
        padding: 38px 30px 64px;
        color: #202630;
      }
      .zw-voices * {
        box-sizing: border-box;
      }
      .zw-voices h1,
      .zw-voices h2,
      .zw-voices h3,
      .zw-voices p {
        margin: 0;
      }
      .zw-voices .zw-voice-topline {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 20px;
        margin-bottom: 24px;
      }
      .zw-voices .zw-voice-eyebrow {
        color: #d6001c;
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.14em;
        text-transform: uppercase;
      }
      .zw-voices h1 {
        margin-top: 8px;
        font-size: clamp(29px, 4vw, 40px);
        font-weight: 750;
        letter-spacing: -0.04em;
        line-height: 1.2;
      }
      .zw-voices .zw-voice-intro {
        margin-top: 11px;
        color: #667085;
        font-size: 14px;
        line-height: 1.7;
        max-width: 720px;
      }
      .zw-voices .zw-voice-link {
        color: #475467;
        font-size: 13px;
        text-decoration: none;
        white-space: nowrap;
      }
      .zw-voices .zw-voice-link:hover {
        color: #d6001c;
        text-decoration: underline;
      }
      .zw-voices .zw-voice-status {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 12px 24px;
        margin: 25px 0 30px;
        padding: 18px 20px;
        border: 1px solid #dbe6df;
        border-radius: 12px;
        background: #f6fbf8;
        color: #365647;
        font-size: 13px;
        line-height: 1.65;
      }
      .zw-voices .zw-voice-status-label {
        display: flex;
        gap: 8px;
        align-items: center;
        color: #176641;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }
      .zw-voices .zw-voice-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
      }
      .zw-voices .zw-voice-status-name {
        margin-top: 3px;
        font-size: 16px;
        font-weight: 700;
        color: #254b36;
        overflow-wrap: anywhere;
      }
      .zw-voices .zw-voice-status-detail {
        color: #66776d;
        font-size: 12px;
      }
      .zw-voices .zw-voice-toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 15px;
        margin-bottom: 20px;
      }
      .zw-voices .zw-voice-library-title {
        font-size: 18px;
        font-weight: 750;
        letter-spacing: -0.02em;
      }
      .zw-voices .zw-voice-catalog-note {
        margin-top: 5px;
        color: #7a8494;
        font-size: 11px;
        line-height: 1.6;
      }
      .zw-voices .zw-voice-count {
        margin-left: 9px;
        color: #7a8494;
        font-size: 12px;
        font-weight: 400;
      }
      .zw-voices .zw-voice-search {
        display: flex;
        align-items: center;
        gap: 9px;
        width: 300px;
        max-width: 100%;
        padding: 0 12px;
        border: 1px solid #d5dce5;
        border-radius: 9px;
        background: #fff;
        color: #7a8494;
      }
      .zw-voices .zw-voice-search input {
        width: 100%;
        min-width: 0;
        height: 42px;
        border: 0;
        background: transparent;
        color: #344054;
        line-height: 1.4;
        font-family: inherit;
        font-size: 13px;
      }
      .zw-voices .zw-voice-search:focus-within {
        outline: 3px solid #ecc3cb;
        outline-offset: 2px;
      }
      .zw-voices .zw-voice-search input:focus {
        outline: none;
      }
      .zw-voices .zw-voice-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 19px;
      }
      .zw-voices .zw-voice-card {
        position: relative;
        display: flex;
        flex-direction: column;
        min-width: 0;
        min-height: 274px;
        padding: 23px;
        border: 1px solid #e1e5ec;
        border-radius: 15px;
        background: #fff;
        box-shadow: 0 3px 12px #20263004;
      }
      .zw-voices .zw-voice-card-original {
        border-color: #eac1c9;
        background: linear-gradient(155deg, #fff6f7 0%, #fff 55%);
      }
      .zw-voices .zw-voice-card-selected {
        box-shadow:
          inset 0 0 0 1px #87b89b,
          0 3px 12px #20263004;
        border-color: #87b89b;
      }
      .zw-voices .zw-voice-card-flags {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        min-height: 22px;
        margin-bottom: 18px;
      }
      .zw-voices .zw-voice-badge {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 3px 7px;
        border-radius: 5px;
        color: #687489;
        background: #f1f3f7;
        font-size: 10px;
        font-weight: 750;
        line-height: 1.5;
        letter-spacing: 0.02em;
      }
      .zw-voices .zw-voice-badge-original {
        color: #b31832;
        background: #ffe9ed;
      }
      .zw-voices .zw-voice-badge-active {
        color: #176641;
        background: #e7f5ed;
      }
      .zw-voices .zw-voice-identity {
        display: flex;
        align-items: center;
        gap: 13px;
        min-width: 0;
      }
      .zw-voices .zw-voice-avatar {
        display: grid;
        place-items: center;
        width: 48px;
        height: 48px;
        flex: 0 0 48px;
        border-radius: 13px;
        background: #f1f4f9;
        color: #596b88;
      }
      .zw-voices .zw-voice-card-original .zw-voice-avatar {
        background: #ffe9ed;
        color: #c81d3b;
      }
      .zw-voices .zw-voice-card h3 {
        font-size: 21px;
        font-weight: 750;
        line-height: 1.3;
        letter-spacing: -0.025em;
        overflow-wrap: anywhere;
      }
      .zw-voices .zw-voice-subtitle {
        margin-top: 4px;
        color: #8791a1;
        font-size: 11px;
        line-height: 1.5;
      }
      .zw-voices .zw-voice-description {
        margin-top: 18px;
        color: #586579;
        font-size: 13px;
        line-height: 1.75;
        overflow-wrap: anywhere;
        white-space: pre-line;
      }
      .zw-voices .zw-voice-description-empty {
        color: #97a0ad;
        font-size: 12px;
      }
      .zw-voices .zw-voice-traits {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 14px;
      }
      .zw-voices .zw-voice-trait {
        padding: 3px 7px;
        border: 1px solid #edf0f4;
        border-radius: 5px;
        background: #fafbfc;
        color: #6d7788;
        font-size: 10px;
        line-height: 1.5;
        overflow-wrap: anywhere;
      }
      .zw-voices .zw-voice-card-actions {
        display: flex;
        gap: 9px;
        margin-top: auto;
        padding-top: 25px;
      }
      .zw-voices .zw-voice-button {
        display: inline-flex;
        justify-content: center;
        align-items: center;
        gap: 7px;
        min-height: 42px;
        padding: 10px 14px;
        border: 1px solid #d5dce5;
        border-radius: 8px;
        background: #fff;
        color: #3b475a;
        font-family: inherit;
        font-size: 12px;
        line-height: 1.4;
        font-weight: 650;
        cursor: pointer;
        transition:
          background 0.15s,
          border-color 0.15s;
      }
      .zw-voices .zw-voice-button:hover:not(:disabled) {
        background: #f5f7fa;
        border-color: #adb8c8;
      }
      .zw-voices .zw-voice-card-actions > button:first-child {
        flex: 1;
      }
      .zw-voices .zw-voice-button-primary {
        color: #fff;
        background: #d6001c;
        border-color: #d6001c;
      }
      .zw-voices .zw-voice-button-primary:hover:not(:disabled) {
        background: #b80018;
        border-color: #b80018;
      }
      .zw-voices .zw-voice-button-selected {
        color: #176641;
        border-color: #c6e0d0;
        background: #f0f8f3;
      }
      .zw-voices button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .zw-voices .zw-voice-button-selected:disabled {
        opacity: 1;
      }
      .zw-voices :is(button, a):focus-visible {
        outline: 3px solid #e8a4b0;
        outline-offset: 3px;
      }
      .zw-voices .zw-voice-message {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 12px;
        margin: 18px 0;
        padding: 14px 17px;
        border: 1px solid #e1e5eb;
        border-radius: 9px;
        background: #fff;
        color: #475467;
        font-size: 13px;
        line-height: 1.6;
      }
      .zw-voices .zw-voice-error {
        border-color: #f1c2c8;
        background: #fff5f6;
        color: #9f1630;
      }
      .zw-voices .zw-voice-success {
        border-color: #bddfcf;
        background: #effaf4;
        color: #176641;
      }
      .zw-voices .zw-voice-footer {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        margin-top: 28px;
        color: #8791a1;
        font-size: 12px;
      }
      .zw-voices .zw-voice-empty {
        padding: 35px 20px;
        border: 1px dashed #d2d9e3;
        border-radius: 12px;
        text-align: center;
        color: #667085;
        font-size: 13px;
        line-height: 1.7;
      }
      .zw-voices .zw-voice-readonly {
        margin: -4px 0 18px;
        color: #667085;
        font-size: 12px;
        line-height: 1.6;
      }
      .zw-voices details {
        margin: 24px 0;
        padding: 15px 18px;
        border: 1px solid #e1e6ee;
        border-radius: 10px;
        background: #fff;
        color: #667085;
        font-size: 12px;
        line-height: 1.8;
      }
      .zw-voices summary {
        color: #536176;
        cursor: pointer;
        font-weight: 600;
      }
      .zw-voices details p {
        margin-top: 10px;
      }
      .zw-voices .zw-voice-spinner {
        display: inline-block;
        width: 14px;
        height: 14px;
        border: 2px solid currentColor;
        border-right-color: transparent;
        border-radius: 50%;
        animation: zw-voice-spin 0.7s linear infinite;
      }
      @keyframes zw-voice-spin {
        to {
          transform: rotate(360deg);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .zw-voices .zw-voice-spinner {
          animation: none;
        }
        .zw-voices .zw-voice-button {
          transition: none;
        }
      }
      @media (min-width: 1350px) {
        .zw-voices .zw-voice-grid {
          grid-template-columns: repeat(4, minmax(0, 1fr));
        }
      }
      @media (max-width: 1000px) {
        .zw-voices .zw-voice-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }
      @media (max-width: 640px) {
        .zw-voices {
          padding: 26px 16px 42px;
        }
        .zw-voices .zw-voice-topline {
          align-items: flex-start;
        }
        .zw-voices .zw-voice-grid {
          grid-template-columns: 1fr;
        }
        .zw-voices .zw-voice-search {
          width: 100%;
        }
        .zw-voices .zw-voice-card {
          min-height: 260px;
        }
        .zw-voices .zw-voice-status {
          margin-bottom: 25px;
          padding: 16px;
        }
      }
    </style>
    <div class="zw-voices">
      <div class="zw-voice-topline">
        <div>
          <p class="zw-voice-eyebrow">Voice Playground</p>
          <h1>Finden Sie Ihre Stimme.</h1>
          <p class="zw-voice-intro">
            Entdecken Sie den gesamten Stimmenkatalog. Hören Sie die echte Begrüßung an und wählen Sie die Stimme für
            Ihre nächsten Anrufe.
          </p>
        </div>
        <a href="/test-lab" class="zw-voice-link">← Settings</a>
      </div>
      ${settings?.enabled
        ? html`
            <div class="zw-voice-status" aria-label="Gespeicherte Stimmauswahl">
              <div>
                <div class="zw-voice-status-label">
                  <span class="zw-voice-dot" aria-hidden="true"></span>Aktive Stimme
                </div>
                <p class="zw-voice-status-name">${voiceFor(settings.currentVoice).name}</p>
              </div>
              <div>
                <p>Für alle neuen Browser- und Telefonanrufe dieser Umgebung.</p>
                <p class="zw-voice-status-detail">
                  Laufende Anrufe behalten ihre Stimme.
                  ${settings.source === 'saved' ? 'Dauerhaft gespeichert.' : 'Umgebungsstandard.'}
                  ${savedDate(settings.updatedAt) ? html` · ${savedDate(settings.updatedAt)}` : nothing}
                </p>
              </div>
            </div>
          `
        : nothing}
      ${notice ? html`<div class="zw-voice-message zw-voice-success" role="status">${notice}</div>` : nothing}
      ${actionError ? html`<div class="zw-voice-message zw-voice-error" role="alert">${actionError}</div>` : nothing}
      ${loadError
        ? html`<div class="zw-voice-message zw-voice-error" role="alert">
            <span>${loadError}</span>
            <button type="button" class="zw-voice-button" ?disabled=${loading} @click=${() => void reload()}>
              Erneut laden
            </button>
          </div>`
        : nothing}
      ${loading && !voices.length
        ? html`<div class="zw-voice-message" role="status">
            Stimmen werden geladen… <span class="zw-voice-spinner" aria-hidden="true"></span>
          </div>`
        : nothing}
      ${settings && !settings.enabled
        ? html`<div class="zw-voice-empty">Die Stimmauswahl ist in dieser Umgebung noch nicht verfügbar.</div>`
        : nothing}
      ${settings?.enabled
        ? html`
            <section aria-labelledby="zw-voice-library" aria-busy=${loading ? 'true' : 'false'}>
              <div class="zw-voice-toolbar">
                <div>
                  <h2 id="zw-voice-library" class="zw-voice-library-title">
                    Alle Stimmen<span class="zw-voice-count">${voices.length}</span>
                  </h2>
                  ${catalogInfo.bundled
                    ? html`<p class="zw-voice-catalog-note">
                        Gespeicherter Soniox-Katalog${catalogInfo.date ? ' · Stand ' + catalogInfo.date : ''}
                      </p>`
                    : nothing}
                </div>
                <label class="zw-voice-search">
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    aria-hidden="true"
                  >
                    <circle cx="10" cy="10" r="6"></circle>
                    <path d="m15 15 5 5"></path>
                  </svg>
                  <input
                    type="search"
                    aria-label="Stimmen nach Name oder Beschreibung suchen"
                    placeholder="Name oder Beschreibung suchen"
                    .value=${search}
                    @input=${(event: Event) => changeSearch((event.target as HTMLInputElement).value)}
                  />
                </label>
              </div>
              ${!settings.canSet
                ? html`<p class="zw-voice-readonly">
                    Sie können alle Stimmen anhören. Das Setzen ist Administratoren vorbehalten.
                  </p>`
                : nothing}
              <div class="zw-voice-grid">
                ${repeat(
                  displayed,
                  voice => voice.id,
                  voice => {
                    const original = voice.id === settings.defaultVoice;
                    const selected = voice.id === settings.currentVoice;
                    const playing = previewVoice === voice.id;
                    return html`<article
                      class="zw-voice-card ${original ? 'zw-voice-card-original' : ''} ${selected
                        ? 'zw-voice-card-selected'
                        : ''}"
                      aria-label=${voice.name}
                    >
                      <div class="zw-voice-card-flags">
                        ${original
                          ? html`<span class="zw-voice-badge zw-voice-badge-original">Originalstimme</span>`
                          : nothing}
                        ${selected
                          ? html`<span class="zw-voice-badge zw-voice-badge-active"
                              ><span class="zw-voice-dot" aria-hidden="true"></span>Aktiv</span
                            >`
                          : nothing}
                        ${!original && !selected
                          ? html`<span class="zw-voice-badge"
                              >${voice.source === 'custom' ? 'Eigene Stimme' : 'Soniox'}</span
                            >`
                          : nothing}
                      </div>
                      <div class="zw-voice-identity">
                        <div class="zw-voice-avatar">${voiceIcon()}</div>
                        <div>
                          <h3>${voice.name}</h3>
                          <p class="zw-voice-subtitle">
                            ${voice.gender
                              ? genderLabel(voice.gender)
                              : voice.source === 'custom'
                                ? 'Eigene Stimme'
                                : 'Soniox'}
                          </p>
                        </div>
                      </div>
                      ${voice.description
                        ? html`<p class="zw-voice-description">${voice.description}</p>`
                        : html`<p class="zw-voice-description zw-voice-description-empty">
                            ${loading ? 'Beschreibung wird geladen…' : 'Keine weitere Beschreibung im Katalog.'}
                          </p>`}
                      ${[voice.age, voice.accent, voice.style, voice.useCase].some(Boolean)
                        ? html`<div class="zw-voice-traits">
                            ${[voice.age, voice.accent, voice.style, voice.useCase]
                              .filter(Boolean)
                              .map(value => html`<span class="zw-voice-trait">${attributeLabel(value)}</span>`)}
                          </div>`
                        : nothing}
                      <div class="zw-voice-card-actions">
                        <button
                          type="button"
                          class="zw-voice-button"
                          aria-pressed=${playing ? 'true' : 'false'}
                          aria-label=${playing ? 'Hörprobe von ' + voice.name + ' stoppen' : voice.name + ' anhören'}
                          @click=${() => void preview(voice)}
                        >
                          ${playing && previewLoading
                            ? html`<span class="zw-voice-spinner" aria-hidden="true"></span>`
                            : playIcon(playing)}
                          ${playing ? (previewLoading ? 'Lädt… · Stop' : 'Stop') : 'Anhören'}
                        </button>
                        <button
                          type="button"
                          class="zw-voice-button ${selected ? 'zw-voice-button-selected' : 'zw-voice-button-primary'}"
                          ?disabled=${selected || !settings.canSet || savingVoice !== null}
                          aria-label=${selected
                            ? voice.name + ' ist ausgewählt'
                            : voice.name + ' für neue Anrufe setzen'}
                          @click=${() => void save(voice)}
                        >
                          ${selected
                            ? 'Ausgewählt'
                            : savingVoice === voice.id
                              ? catalogInfo.bundled
                                ? 'Prüfe Stimme…'
                                : 'Speichere…'
                              : 'Set'}
                        </button>
                      </div>
                    </article>`;
                  },
                )}
              </div>
              ${term && !matches.length
                ? html`<p class="zw-voice-readonly" style="margin-top: 18px">
                    Keine weiteren Treffer. Originalstimme und aktive Auswahl bleiben sichtbar.
                  </p>`
                : nothing}
              <div class="zw-voice-footer" ${ref(sentinel)}>
                <span role="status">${displayed.length} von ${ordered.length} Stimmen angezeigt</span>
                ${displayed.length < ordered.length
                  ? html`<button type="button" class="zw-voice-button" @click=${loadMore}>
                      Weitere Stimmen laden
                    </button>`
                  : nothing}
              </div>
            </section>
            <details>
              <summary>Welche Begrüßung höre ich?</summary>
              <p>
                Jede Hörprobe spricht die aktuelle deutsch-englische Begrüßung mit der gewählten Stimme. „Anhören“
                ändert die Auswahl nicht. Erst „Set“ speichert sie für alle neuen Anrufe dieser Umgebung.
              </p>
              ${previewText ? html`<p>${previewText}</p>` : nothing}
            </details>
          `
        : nothing}
    </div>
  `;
});
